import axios from 'axios'

export interface ChatConversation {
  conversation_id: string
  merchant_id: string
  user_id: string
  subject: string
  status: 'active' | 'archived' | 'closed'
  last_message?: ChatMessage
  unread_count: number
  user_name?: string
  user_email?: string
  created_at: string
  updated_at: string
}

export interface ChatMessage {
  message_id: string
  conversation_id: string
  sender_id: string
  sender_type: 'user' | 'merchant'
  content: string
  message_type: 'text' | 'image' | 'file'
  file_url?: string
  file_name?: string
  created_at: string
}

export interface SendMessagePayload {
  content: string
  message_type?: 'text' | 'image' | 'file'
  file_url?: string
  file_name?: string
}

export interface CreateConversationPayload {
  merchant_id: string
  subject: string
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const chatApi = axios.create({
  baseURL: `${API_BASE_URL}/chat`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
chatApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
chatApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const chatService = {
  // Get all conversations (admin/merchant view)
  async getConversations(params?: {
    merchant_id?: string
    status?: string
    search?: string
    page?: number
    limit?: number
  }): Promise<ChatConversation[]> {
    try {
      const response = await chatApi.get('/conversations', { params })
      return response.data.data || []
    } catch (error) {
      console.error('Erreur lors de la récupération des conversations:', error)
      return []
    }
  },

  // Get messages for a conversation
  async getMessages(
    conversationId: string,
    page: number = 1,
    limit: number = 50
  ): Promise<PaginatedResponse<ChatMessage>> {
    try {
      const response = await chatApi.get(`/conversations/${conversationId}/messages`, {
        params: { page, limit }
      })
      return {
        success: response.data.success,
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        limit: response.data.limit || limit,
        total_pages: response.data.total_pages || 0
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des messages:', error)
      return {
        success: false,
        data: [],
        total: 0,
        page,
        limit,
        total_pages: 0
      }
    }
  },

  // Send a message as merchant
  async sendMessage(
    conversationId: string,
    payload: SendMessagePayload
  ): Promise<ChatMessage | null> {
    try {
      const response = await chatApi.post(`/conversations/${conversationId}/messages`, payload)
      return response.data.data || null
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error)
      return null
    }
  },

  // Mark conversation as read
  async markAsRead(conversationId: string): Promise<boolean> {
    try {
      const response = await chatApi.post(`/conversations/${conversationId}/read-all`)
      return response.data.success === true
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error)
      return false
    }
  },

  // Archive a conversation
  async archiveConversation(conversationId: string): Promise<boolean> {
    try {
      const response = await chatApi.patch(`/conversations/${conversationId}`, {
        status: 'archived'
      })
      return response.data.success === true
    } catch (error) {
      console.error('Erreur lors de l\'archivage de la conversation:', error)
      return false
    }
  },

  // Close a conversation
  async closeConversation(conversationId: string): Promise<boolean> {
    try {
      const response = await chatApi.patch(`/conversations/${conversationId}`, {
        status: 'closed'
      })
      return response.data.success === true
    } catch (error) {
      console.error('Erreur lors de la fermeture de la conversation:', error)
      return false
    }
  },

  // Get unread count
  async getUnreadCount(merchantId?: string): Promise<number> {
    try {
      const params = merchantId ? { merchant_id: merchantId } : {}
      const response = await chatApi.get('/conversations', { params })
      const conversations: ChatConversation[] = response.data.data || []
      return conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0)
    } catch (error) {
      console.error('Erreur lors du calcul des messages non lus:', error)
      return 0
    }
  }
}
